/**
 * Canonical feed time and id key.
 *
 * `toEpochMs` returns UTC epoch milliseconds. MySQL DATETIME text (space
 * separator, no zone) and ISO-8601 datetimes with no zone are UTC, matching
 * the backend connection `time_zone='+00:00'`. Z and numeric offsets are
 * applied. Unparseable values return null.
 *
 * Feed order matches SQL `ORDER BY utc_timestamp DESC, id DESC` when ids are
 * compared case-insensitively (hex UUIDs under a ci collation).
 */

const LOCAL_UTC_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

export function idKey(id: string): string {
  return id.toLowerCase();
}

export function toEpochMs(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }
  const text = value.trim();
  if (!text) {
    return null;
  }
  if (/[zZ]$/.test(text) || /[+-]\d{2}:\d{2}$/.test(text)) {
    const parsed = Date.parse(text);
    return Number.isFinite(parsed) ? parsed : null;
  }
  const match = LOCAL_UTC_RE.exec(text);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const fraction = match[7] ?? '';
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }
  const millis =
    fraction.length === 0 ? 0 : Number(fraction.slice(0, 3).padEnd(3, '0'));
  if (!Number.isFinite(millis)) {
    return null;
  }
  const epoch = Date.UTC(year, month - 1, day, hour, minute, second, millis);
  const check = new Date(epoch);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return null;
  }
  return epoch;
}

/** Comparator for `ORDER BY utc_timestamp DESC, id DESC`. */
export function compareFeedOrder(
  a: { sortTs: number; idKey: string },
  b: { sortTs: number; idKey: string }
): number {
  if (a.sortTs !== b.sortTs) {
    return b.sortTs - a.sortTs;
  }
  if (a.idKey === b.idKey) {
    return 0;
  }
  return a.idKey < b.idKey ? 1 : -1;
}
